/**
 * Corrosion Visualization Shader Uniforms Manager
 *
 * This module manages corrosion data via a global object that is read by
 * the render loop in Potree. This decouples corrosion data from Potree's
 * material system, avoiding issues with material resets during shader
 * recompilation.
 *
 * The actual shader code lives in:
 * public/potree-measurements/libs/potree/potree.js
 * - Vertex shader: Lines 57539-58536
 * - Fragment shader: Lines 58547-58647
 */

import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';

const THREE = window.THREE;

/**
 * Initialize/ensure the global corrosion data object exists
 *
 * Data is stored in window.AVA_CORROSION_DATA and read by the render loop
 * This avoids issues with material uniforms being reset during shader recompilation
 */
function ensureGlobalCorrosionData() {
  if (!window.AVA_CORROSION_DATA) {
    window.AVA_CORROSION_DATA = {
      enabled: false,
      cylinderCenter: [0, 0, 0],
      cylinderAxis: [0, 1, 0],
      cylinderRadius: 1.0,
      maxDistance: 0.015,
      // 4 gradient colors (RGB arrays) - aligned with 4 severity stages
      gradientColors: [
        [0, 1, 0],       // Green (Cat A: 0-4mm) - Stage 0
        [1, 1, 0],       // Yellow (Cat B: 4-6mm) - Stage 1
        [1, 0.647, 0],   // Orange (Cat C: 6-9mm) - Stage 2
        [1, 0, 0]        // Red (Cat D: 9mm+) - Stage 3
      ],
      // 4 gradient positions (0.0 to 1.0)
      gradientPositions: [0.0, 0.33, 0.66, 1.0],
      // Visibility flags for each classification level
      visibilityFlags: {
        normal: 1,      // 0-4mm (Cat A - Green) - 1 = visible, 0 = hidden
        minorWear: 1,   // 4-6mm (Cat B - Yellow)
        moderate: 1,    // 6-9mm (Cat C - Orange)
        critical: 1     // 9mm+ (Cat D - Red)
      },
      // Threshold values for classification (in meters) - aligned with enums.js
      thresholds: {
        normalToMinor: 0.004,    // 4mm - Cat A -> Cat B
        minorToModerate: 0.006,  // 6mm - Cat B -> Cat C
        moderateToCritical: 0.009 // 9mm - Cat C -> Cat D
      }
    };
  }

  return window.AVA_CORROSION_DATA;
}

/**
 * Apply corrosion shader to Potree material
 *
 * @param {Object|Object[]} materialInput - Potree PointCloudMaterial or array of materials
 * @param {Object} cylinderData - {position, axis, radius}
 * @param {Array} gradient - Gradient array from corrosionGradient.js
 * @param {number} maxDistance - Maximum distance for normalization (meters)
 */
export function applyCorrosionShader(materialInput, cylinderData, gradient, maxDistance = 0.015) {
  const materials = Array.isArray(materialInput)
    ? materialInput.filter(Boolean)
    : [materialInput];

  if (!materials.length) {
    throw new Error('At least one material is required to apply corrosion shader');
  }

  // Ensure global data object exists
  const globalData = ensureGlobalCorrosionData();

  // Prepare normalized axis
  const axisNormalized = cylinderData.axis.clone().normalize();

  // Write cylinder parameters to GLOBAL object (not material uniforms)
  globalData.cylinderCenter = [
    cylinderData.position.x,
    cylinderData.position.y,
    cylinderData.position.z
  ];

  globalData.cylinderAxis = [
    axisNormalized.x,
    axisNormalized.y,
    axisNormalized.z
  ];

  globalData.cylinderRadius = cylinderData.radius;
  globalData.maxDistance = maxDistance;

  // Initialize visibility flags (all visible by default)
  globalData.visibilityFlags = {
    normal: 1,
    minorWear: 1,
    moderate: 1,
    critical: 1
  };

  // Initialize threshold values for classification
  globalData.thresholds = {
    normalToMinor: 0.004,    // 4mm - Cat A -> Cat B
    minorToModerate: 0.006,  // 6mm - Cat B -> Cat C
    moderateToCritical: 0.009 // 9mm - Cat C -> Cat D
  };

  // Write gradient colors to GLOBAL object (expecting 4 color stops)
  if (gradient.length >= 4) {
    const colors = gradient.map(stop => stop[1]); // Extract THREE.Color objects

    // Set all 4 gradient colors as RGB arrays
    globalData.gradientColors = [
      [colors[0].r, colors[0].g, colors[0].b],
      [colors[1].r, colors[1].g, colors[1].b],
      [colors[2].r, colors[2].g, colors[2].b],
      [colors[3].r, colors[3].g, colors[3].b]
    ];

    // Set all 4 gradient positions
    globalData.gradientPositions = [
      gradient[0][0],
      gradient[1][0],
      gradient[2][0],
      gradient[3][0]
    ];
  }

  // Enable corrosion mode in global object
  globalData.enabled = true;

  // CRITICAL: Set activeAttributeName to trigger shader recompilation
  // This recompiles the shader with #define color_type_corrosion
  materials.forEach((material) => {
    if (!material) {
      return;
    }

    material.activeAttributeName = 'corrosion';
    material.needsUpdate = true;
  });
}

/**
 * Disable corrosion shader visualization
 *
 * @param {Object[]} materials - Array of Potree PointCloudMaterials (used for activeAttributeName only)
 */
export function disableCorrosionShader(materials) {
  materials = Array.isArray(materials) ? materials : [materials];

  // Disable corrosion mode in GLOBAL object
  if (window.AVA_CORROSION_DATA) {
    window.AVA_CORROSION_DATA.enabled = false;
  }

  // Reset material to default color mode (typically 'rgba')
  materials.forEach(material => {
    material.activeAttributeName = 'rgba';
    material.needsUpdate = true;
  });
}

/**
 * Update gradient style for active corrosion visualization
 *
 * @param {Object} material - Potree PointCloudMaterial (used for activeAttributeName check only)
 * @param {Array} gradient - New gradient array
 */
export function updateCorrosionGradient(material, gradient) {
  // Check if corrosion mode is enabled
  if (material.activeAttributeName !== 'corrosion') {
    return;
  }

  // Check if global data object exists
  if (!window.AVA_CORROSION_DATA) {
    return;
  }

  if (gradient.length >= 4) {
    const colors = gradient.map(stop => stop[1]); // Extract THREE.Color objects

    // Update gradient colors in GLOBAL object (not material uniforms)
    window.AVA_CORROSION_DATA.gradientColors = [
      [colors[0].r, colors[0].g, colors[0].b],
      [colors[1].r, colors[1].g, colors[1].b],
      [colors[2].r, colors[2].g, colors[2].b],
      [colors[3].r, colors[3].g, colors[3].b]
    ];

    // Update gradient positions in GLOBAL object
    window.AVA_CORROSION_DATA.gradientPositions = [
      gradient[0][0],
      gradient[1][0],
      gradient[2][0],
      gradient[3][0]
    ];
  }
}

/**
 * Update both gradient and thresholds dynamically
 * This allows real-time adjustment without re-enabling corrosion mode
 *
 * @param {Object} material - Potree PointCloudMaterial
 * @param {Object} cylinderData - Updated cylinder parameters
 * @param {Array} gradient - New gradient array
 * @param {number} maxDistance - New maximum distance for normalization (meters)
 */
export function updateCorrosionThresholds(material, cylinderData, gradient, maxDistance) {
  // Check if corrosion mode is enabled
  if (material.activeAttributeName !== 'corrosion') {
    return false;
  }

  // Check if global data object exists
  const globalData = ensureGlobalCorrosionData();
  if (!globalData.enabled) {
    return false;
  }

  // Update cylinder parameters (if provided)
  if (cylinderData) {
    const axisNormalized = cylinderData.axis.clone().normalize();

    globalData.cylinderCenter = [
      cylinderData.position.x,
      cylinderData.position.y,
      cylinderData.position.z
    ];

    globalData.cylinderAxis = [
      axisNormalized.x,
      axisNormalized.y,
      axisNormalized.z
    ];

    globalData.cylinderRadius = cylinderData.radius;
  }

  // Update max distance
  if (maxDistance !== undefined && maxDistance > 0) {
    globalData.maxDistance = maxDistance;
  }

  // Update gradient
  if (gradient && gradient.length >= 4) {
    const colors = gradient.map(stop => stop[1]); // Extract THREE.Color objects

    globalData.gradientColors = [
      [colors[0].r, colors[0].g, colors[0].b],
      [colors[1].r, colors[1].g, colors[1].b],
      [colors[2].r, colors[2].g, colors[2].b],
      [colors[3].r, colors[3].g, colors[3].b]
    ];

    globalData.gradientPositions = [
      gradient[0][0],
      gradient[1][0],
      gradient[2][0],
      gradient[3][0]
    ];
  }

  // Force material update to trigger re-render
  material.needsUpdate = true;

  return true;
}

/**
 * Get current corrosion thresholds
 *
 * @returns {Object|null} Current thresholds or null if not initialized
 */
export function getCurrentThresholds() {
  if (!window.AVA_CORROSION_DATA || !window.AVA_CORROSION_DATA.enabled) {
    return null;
  }

  return {
    maxDistance: window.AVA_CORROSION_DATA.maxDistance,
    cylinderRadius: window.AVA_CORROSION_DATA.cylinderRadius,
    gradientColors: window.AVA_CORROSION_DATA.gradientColors,
    gradientPositions: window.AVA_CORROSION_DATA.gradientPositions
  };
}

/**
 * Check if corrosion shader is currently enabled
 *
 * @param {Object} material - Potree PointCloudMaterial
 * @returns {boolean}
 */
export function isCorrosionEnabled(material) {
  // v1.0.7: Check activeAttributeName instead of uniform (compile-time approach)
  return material.activeAttributeName === 'corrosion';
}

/**
 * Update visibility flags for classification levels
 * This allows toggling visibility of specific corrosion severity levels
 *
 * @param {Object} visibilitySettings - Object with boolean values for each level
 *   {normal: boolean, minorWear: boolean, moderate: boolean, critical: boolean}
 * @param {Object} material - Potree PointCloudMaterial (optional, for triggering update)
 * @returns {boolean} True if update was successful
 */
export function updateVisibilityFlags(visibilitySettings, material = null) {
  // Ensure global data exists
  const globalData = ensureGlobalCorrosionData();

  if (!globalData.enabled) {
    debugWarn('[Corrosion Shader] Cannot update visibility - corrosion mode not enabled');
    return false;
  }

  // Update visibility flags (convert boolean to 0/1 for shader)
  globalData.visibilityFlags = {
    normal: visibilitySettings.normal ? 1 : 0,
    minorWear: visibilitySettings.minorWear ? 1 : 0,
    moderate: visibilitySettings.moderate ? 1 : 0,
    critical: visibilitySettings.critical ? 1 : 0
  };

  debugLog('[Corrosion Shader] Visibility flags updated:', globalData.visibilityFlags);

  // Trigger material update if provided
  if (material) {
    material.needsUpdate = true;
  }

  return true;
}

/**
 * Update threshold values used for classification
 * This should be called when user adjusts thresholds in the UI
 *
 * @param {Object} thresholdsInMeters - Threshold values in meters
 * @returns {boolean} True if update was successful
 */
export function updateClassificationThresholds(thresholdsInMeters) {
  const globalData = ensureGlobalCorrosionData();

  if (!globalData.enabled) {
    debugWarn('[Corrosion Shader] Cannot update thresholds - corrosion mode not enabled');
    return false;
  }

  // Update threshold values
  globalData.thresholds = {
    normalToMinor: thresholdsInMeters.normalToMinor,
    minorToModerate: thresholdsInMeters.minorToModerate,
    moderateToCritical: thresholdsInMeters.moderateToCritical
  };

  debugLog('[Corrosion Shader] Classification thresholds updated:', globalData.thresholds);

  return true;
}

/**
 * Get current visibility flags
 *
 * @returns {Object|null} Current visibility flags or null if not initialized
 */
export function getVisibilityFlags() {
  if (!window.AVA_CORROSION_DATA || !window.AVA_CORROSION_DATA.enabled) {
    return null;
  }

  return { ...window.AVA_CORROSION_DATA.visibilityFlags };
}
